import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as GetAppIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import type { DocumentActionButtonsProps } from '../types';

/**
 * DocumentActionButtons - Action buttons for a document
 *
 * Pure presentational component that displays action buttons:
 * - View: Always visible
 * - Download: Only for custom (non-SAP) documents
 * - Edit: Only if user has edit permission
 * - Delete: Only if user has delete permission
 *
 * @param doc - The document to display actions for
 * @param canEdit - Whether user can edit this document
 * @param canDelete - Whether user can delete this document
 * @param onView - Callback when view button is clicked
 * @param onDownload - Callback when download button is clicked
 * @param onEdit - Callback when edit button is clicked
 * @param onDelete - Callback when delete button is clicked
 */
export const DocumentActionButtons: React.FC<DocumentActionButtonsProps> = ({
  doc,
  canEdit,
  canDelete,
  onView,
  onDownload,
  onEdit,
  onDelete
}) => {
  const { t } = useTranslation();
  const isSap = 'source_type' in doc && doc.source_type === 'sap';

  return (
    <>
      {/* View in Browser Button */}
      <IconButton
        color="primary"
        onClick={() => onView(doc)}
        title={t('documents.table.view')}
        aria-label={t('documents.table.view')}
        size="small"
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>

      {/* Download Button - only for custom documents */}
      {!isSap && (
        <IconButton
          color="primary"
          onClick={() => onDownload(doc)}
          title={t('documents.table.download')}
          aria-label={t('documents.table.download')}
          size="small"
        >
          <GetAppIcon fontSize="small" />
        </IconButton>
      )}

      {/* Edit Button - only if user has permission */}
      {canEdit && (
        <IconButton
          color="primary"
          onClick={() => onEdit(doc)}
          title={t('documents.table.edit')}
          aria-label={t('documents.table.edit')}
          size="small"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}

      {/* Delete Button - only if user has permission */}
      {canDelete && (
        <IconButton
          color="error"
          onClick={() => onDelete(doc.document_id || '')}
          title={t('documents.table.delete')}
          aria-label={t('documents.table.delete')}
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </>
  );
};

DocumentActionButtons.displayName = 'DocumentActionButtons';
